import React from 'react';
import Box from '@material-ui/core/Box';
import { Field, FieldProps } from 'react-final-form';

import { makeStyles } from '@material-ui/core/styles';
import InputLabel from '@material-ui/core/InputLabel';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

import { ReactComponent as DownloadIcon } from 'assets/general/icons/Download.svg';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    marginTop: '16px',
  },
  fileInputContainer: {
    width: '100%',
    height: '55px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      borderColor: '#212121',
      borderWidth: '2px',
    },
    '&:focus-within': {
      borderColor: '#6C13F9',
    },
  },
  input: {
    opacity: 0,
    position: 'absolute',
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    zIndex: 1,
  },
  label: {
    position: 'absolute',
    left: 0,
    color: '#969AB2',
    fontSize: 16,
    transform: 'translate(0, 22px)',
    transition:
      'transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
    transformOrigin: 'left top',
    pointerEvents: 'none',
    background: '#fff',
    zIndex: 2,
    '&.focused, .has-value &': {
      transform: 'translate(0, -1.5px) scale(0.75)',
      color: '#1976d2',
    },
  },
  fileName: {
    color: '#000',
    fontSize: '14px',
    position: 'relative',
    zIndex: 1,
    marginTop: '4px',
  },
  clearButton: {
    padding: 8,
    marginRight: -8,
    zIndex: 2,
    '& svg': {
      fontSize: 20,
    },
  },
  fileNameContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    position: 'relative',
    zIndex: 1,
    padding: '4px 0',
  },
  downloadIcon: {
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    cursor: 'pointer',
  },
}));

interface IFileInputProps {
  name: string;
  accept?: string;
  onChange?: (file: File) => void;
  label?: string;
}

const FileInput: React.FC<FieldProps<any, any> & IFileInputProps> = ({
  name,
  accept = 'image/*',
  onChange,
  label = 'Add your logo',
  ...rest
}) => {
  const classes = useStyles();
  const [fileName, setFileName] = React.useState<string | null>(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (value: any) => void
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      fieldOnChange(file);
      if (onChange) onChange(file);
      setFileName(file.name);
    }
  };

  const handleClear = (fieldOnChange: (value: any) => void, event: React.MouseEvent) => {
    event.stopPropagation();
    setFileName(null);
    fieldOnChange(null);
    if (onChange) onChange(null as any);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleDownloadClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    inputRef.current?.click();
  };

  return (
    <Field
      name={name}
      render={({ input: { value, onChange } }) => (
        <Box className={`${classes.container} ${fileName ? 'has-value' : ''}`}>
          <InputLabel className={`${classes.label} ${isFocused ? 'focused' : ''}`}>
            {label}
          </InputLabel>
          <Box className={classes.fileInputContainer}>
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              className={classes.input}
              onChange={(e) => handleFileChange(e, onChange)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
            {fileName ? (
              <div className={classes.fileNameContainer}>
                <span className={classes.fileName}>{fileName}</span>
                <IconButton
                  className={classes.clearButton}
                  onClick={(e) => handleClear(onChange, e)}
                  size="small">
                  <CloseIcon />
                </IconButton>
              </div>
            ) : (
              <DownloadIcon className={classes.downloadIcon} onClick={handleDownloadClick} />
            )}
          </Box>
        </Box>
      )}
      {...rest}
    />
  );
};

export default FileInput;
